import type { FC } from 'react';
import React, { useCallback, memo, useMemo } from 'react';
import { graphql } from 'react-relay';
import { type GraphQLTaggedNode } from 'relay-runtime';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { LoadableAfterPaint } from '@confluence/loadable';

import type { SpaceNavigationQuery_spaceSidebarLinks_main } from './__types__/SpaceNavigationQuery.ts';
import { CALENDARS_KEY, QUESTIONS_NATIVE_KEY } from './webItemCompleteKeys.ts';

export const SpaceLinksSpaceSidebarLinksFragment: GraphQLTaggedNode = graphql`
	fragment SpaceLinks_spaceSidebarLinks on SpaceSidebarLinks {
		main(includeHidden: true) {
			id
			webItemCompleteKey
			position
			hidden
		}
	}
`;

type SpaceLinksProps = {
	links: SpaceNavigationQuery_spaceSidebarLinks_main[];
};

export const SpaceLinks: FC<SpaceLinksProps> = memo(({ links }) => {
	const routeDataRef = useRouteDataRef();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { edition } = useSessionData();

	const isPremiumEdition = edition === ConfluenceEdition.PREMIUM;

	const sendItemClickedAnalytics = useCallback(
		(itemId: string) => () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'navigationItem',
					actionSubjectId: 'spaceNavigationItem',
					source: 'containerNavigation',
					attributes: {
						navigationLayer: 'container',
						itemId,
						selectedItemPageContext: routeDataRef.current?.routeName,
						navVersion: '4',
						isPremiumEdition,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, routeDataRef, isPremiumEdition],
	);

	const renderedLinks = useMemo(() => {
		const builtInitemsMapping: Record<string, React.ReactNode> = {
			[CALENDARS_KEY]: (
				<CalendarsLink key="calendars-link" onClick={sendItemClickedAnalytics('calendars')} />
			),
			[QUESTIONS_NATIVE_KEY]: (
				<QuestionsLink
					key="questions-native-link"
					onClick={sendItemClickedAnalytics('questions')}
				/>
			),
		};

		return links
			.filter((link): link is SpaceNavigationQuery_spaceSidebarLinks_main =>
				Boolean(link && !link.hidden),
			)
			.sort((a, b) => Number(a.position) - Number(b.position))
			.map(
				({ webItemCompleteKey }) =>
					(webItemCompleteKey && builtInitemsMapping[webItemCompleteKey]) ||
					/* Hide everything else */ null,
			);
	}, [links, sendItemClickedAnalytics]);

	return <>{renderedLinks}</>;
});

const CalendarsLink = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CalendarsLink" */ './CalendarsLink.tsx'))
			.CalendarsLink,
});

const QuestionsLink = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-QuestionsLink" */ './QuestionsLink.tsx'))
			.QuestionsLink,
});
